import React, { useCallback, useState } from 'react';
import { Pencil, X, Check, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { updateMaterialityAssessmentReport } from './assessmentReportsEditApi.js';

export default function CompanyProfileEditCard({ assessment, materialityData, onSaved }) {
  const profile = materialityData?.companyProfile;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [growthStage, setGrowthStage] = useState('');

  const initFromProfile = useCallback(() => {
    const p = materialityData?.companyProfile || {};
    setCompanyName(String(p.companyName ?? ''));
    setIndustry(String(p.industry ?? ''));
    setGrowthStage(String(p.growthStage ?? ''));
  }, [materialityData]);

  const startEdit = () => {
    initFromProfile();
    setEditing(true);
  };

  const cancelEdit = () => {
    initFromProfile();
    setEditing(false);
  };

  const handleSave = async () => {
    if (!assessment?.id || !materialityData?.companyProfile) return;
    const name = String(companyName || '').trim();
    if (!name) {
      toast.error('Company name is required');
      return;
    }
    try {
      setSaving(true);
      const nextMateriality = {
        ...materialityData,
        companyProfile: {
          ...materialityData.companyProfile,
          companyName: name,
          industry: String(industry || '').trim(),
          growthStage: String(growthStage || '').trim(),
        },
      };
      const payload = {
        analysis_name: assessment.analysis_name,
        documents_json: assessment.documents_json || null,
        materiality_json: nextMateriality,
        sox_cycle_json: assessment.sox_cycle_json,
        entityscoping_json: assessment.entityscoping_json ?? null,
        ...(assessment.audit_year_id ? { audit_year_id: assessment.audit_year_id } : {}),
      };
      const data = await updateMaterialityAssessmentReport(assessment.id, payload);
      if (!data?.success && !data?.id) {
        throw new Error(data?.error || 'Update failed');
      }
      toast.success('Company profile updated');
      setEditing(false);
      onSaved?.({
        ...assessment,
        ...payload,
        id: assessment.id,
        materiality_json: nextMateriality,
      });
    } catch (err) {
      toast.error(
        resolve403ErrorMessage(err, err?.response?.data?.error || err.message || 'Failed to update')
      );
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Company Profile</h2>
        {!editing ? (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-600 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Company Name</label>
            <p className="text-lg font-semibold text-gray-900">{profile.companyName}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Industry</label>
            <p className="text-lg text-gray-900">{profile.industry}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Growth Stage</label>
            <p className="text-lg text-gray-900">{profile.growthStage}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Company Name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Industry</label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Growth Stage</label>
            <input
              type="text"
              value={growthStage}
              onChange={(e) => setGrowthStage(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      )}
    </div>
  );
}
